import { z } from "zod";

/**
 * A workspace's email suppression list (PREC-4266): addresses that workspace's sends refuse.
 *
 * A row is evidence about one address in one workspace (`email_suppression`, unique on
 * `(companyId, email)`), written when a mail from one of the workspace's mailboxes bounced. It is
 * kept apart from the Blacklist and from DNC: a blacklist entry is an intentional, legally
 * permanent decision, a suppression is a delivery fact the user may remove on purpose. The
 * prospect row's global `BOUNCED_*` status stays as information only; another workspace that
 * holds the same person is not refused by this workspace's bounce.
 *
 * - `HARD_BOUNCE`    – the receiving server refused the address permanently (every bounce before
 *                      slice 7 of the email settings).
 * - `PROVIDER_BLOCK` – the receiving provider blocked the sender rather than the address (slice 7).
 */
export const EMAIL_SUPPRESSION_REASONS = ["HARD_BOUNCE", "PROVIDER_BLOCK"] as const;

export type EmailSuppressionReason = (typeof EMAIL_SUPPRESSION_REASONS)[number];

export const emailSuppressionReasonSchema = z.enum(EMAIL_SUPPRESSION_REASONS);

/**
 * The address as a suppression stores it and as every lookup asks for it: trimmed and
 * lower-cased, `null` when there is none. The writer, the send gate and the backfill
 * (`LOWER(TRIM(email))`) all key on this, so `Jane@Example.com` and `jane@example.com` are one row.
 */
export function normaliseSuppressedEmail(email?: string | null): string | null {
	const normalised = (email ?? "").trim().toLowerCase();

	return normalised || null;
}

/** One row of the Suppressed tab (`GET contact/suppressions`). */
export const emailSuppressionRowSchema = z.object({
	id: z.number(),
	/** The address that was suppressed, as stored, even when the lead's address changed since. */
	email: z.string(),
	reason: emailSuppressionReasonSchema,
	/** The SMTP status code of the bounce (`5.1.1`); null until slice 7 records it. */
	smtpCode: z.string().nullable(),
	suppressedAt: z.string(),
	prospectId: z.number().nullable(),
	prospectName: z.string().nullable(),
	organizationName: z.string().nullable(),
});

export type EmailSuppressionRow = z.infer<typeof emailSuppressionRowSchema>;

/** The answer to `DELETE contact/suppressions/:id`: the row that is no longer suppressed. */
export const emailSuppressionRemovedSchema = z.object({
	id: z.number(),
	email: z.string(),
	removedAt: z.string(),
});

export type EmailSuppressionRemoved = z.infer<typeof emailSuppressionRemovedSchema>;
